// src/pages/visit/VisitDetailDialog.tsx
import React from "react";
import { Dialog } from "primereact/dialog";
import { 
  Clock, 
  ShoppingCart, 
  Wallet, 
  MapPin, 
  FileText, 
  UserCheck, 
  X 
} from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { VisitRoute, VisitAction } from "../../../types/visit";
import { Tag } from "primereact/tag";

interface VisitDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  visit: VisitRoute | null;
}

export const VisitDetailDialog: React.FC<VisitDetailDialogProps> = ({
  isOpen,
  onClose,
  visit,
}) => {
  if (!visit) return null;

  // Aksiyon tipine göre ikon ve renk belirleme
  const getActionIcon = (type: VisitAction["type"]) => {
    switch (type) {
      case "MEETING": return <UserCheck className="text-blue-500" size={20} />;
      case "ORDER": return <ShoppingCart className="text-orange-500" size={20} />;
      case "PAYMENT": return <Wallet className="text-green-500" size={20} />;
      default: return <FileText size={20} />;
    }
  };

  return (
    <Dialog
      header={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
             <MapPin className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-bold">{visit.account?.name}</h3>
            <p className="text-xs text-gray-500">{visit.address?.title || "Merkez Adres"}</p>
          </div>
        </div>
      }
      visible={isOpen}
      onHide={onClose}
      modal
      className="w-full lg:w-5/12"
      footer={
        <div className="flex justify-end">
          <IconTextButton onClick={onClose} icon={X} className="btn-cancel">Kapat</IconTextButton>
        </div>
      }
    >
      <div className="space-y-6 py-4">
        {/* Üst Bilgi Kartları */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <span className="text-xs text-blue-600 block mb-1">Planlanan Tarih</span>
            <span className="font-semibold">{new Date(visit.plannedDate).toLocaleDateString('tr-TR')}</span>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
            <span className="text-xs text-purple-600 block mb-1">Ziyaret Durumu</span>
            <Tag value={visit.status} severity={visit.status === 'COMPLETED' ? 'success' : 'info'} />
          </div>
        </div>

        {/* Aksiyon Listesi (Timeline Mantığı) */}
        <div className="space-y-4">
          <h4 className="font-bold flex items-center gap-2 border-b pb-2 text-gray-700">
            <Clock size={18} /> Ziyaret Hareketleri
          </h4>
          
          {visit.actions && visit.actions.length > 0 ? (
            <div className="relative border-l-2 border-gray-200 ml-3 pl-6 space-y-6">
              {visit.actions.map((action, idx) => (
                <div key={idx} className="relative">
                  {/* Timeline Noktası */}
                  <div className="absolute -left-[33px] bg-white p-1 rounded-full border-2 border-gray-200">
                    {getActionIcon(action.type)}
                  </div>
                  
                  {/* Aksiyon Kartı */}
                  <div className="bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-bold text-gray-800">
                        {action.type === "MEETING" ? "Ziyaret Giriş/Çıkış" : 
                         action.type === "ORDER" ? "Sipariş Alındı" : "Tahsilat Yapıldı"}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(action.createdAt || "").toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    {/* Aksiyon Detay İçeriği */}
                    <div className="text-sm text-gray-600">
                      {action.type === "ORDER" && action.order && (
                        <div className="bg-orange-50 p-2 rounded border border-orange-100 flex justify-between">
                          <span>Sipariş: <b className="text-orange-700">{action.order.orderNumber}</b></span>
                          <b className="text-orange-800">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(action.order.totalAmount)}</b>
                        </div>
                      )}

                      {action.type === "PAYMENT" && action.movement && (
                        <div className="bg-green-50 p-2 rounded border border-green-100 flex justify-between">
                          <span>Fiş/Makbuz: <b className="text-green-700">{action.movement.number}</b></span>
                          <b className="text-green-800">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(action.movement.amount)}</b>
                        </div>
                      )}

                      {action.description && (
                        <div className="mt-2 flex items-start gap-2 bg-gray-50 p-2 rounded italic">
                          <FileText size={14} className="mt-1 text-gray-400" />
                          <span>{action.description}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-400">
              <p>Bu ziyarete ait henüz bir aktivite bulunmuyor.</p>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};